import React, { useState, useRef, useEffect, FormEvent } from 'react';
import axios, { AxiosError } from 'axios';
import { SlLogin } from 'react-icons/sl';
import {
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Link,
  Snackbar,
  Alert,
  Slide
} from '@mui/material';

import { User } from '../types';

interface LoginProps {
  setUser: (user: User | null) => void;
  onClose: () => void;
}

type AlertColor = 'success' | 'info' | 'warning' | 'error';

interface ErrorResponse {
  message: string;
}

const Login: React.FC<LoginProps> = ({ setUser, onClose }) => {
  const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoginMode, setIsLoginMode] = useState<boolean>(true);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('info');

  const isMounted = useRef(true);

  const handleSnackbarClose = () => setOpenSnackbar(false);

  const showFeedback = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
    if (severity === 'error' || severity === 'warning') console.error(message);
  };

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
  };

  const saveUserData = (userData: User) => {
    sessionStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.setItem('token', userData.token);

    const backup = { ...userData, expiry: new Date().getTime() + 10 * 60 * 1000 };
    localStorage.setItem('user', JSON.stringify(backup));
    localStorage.setItem('token', userData.token);
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${apiBaseUrl}/api/auth/login`, {
        username: username.trim(),
        password: password.trim()
      });

      if (response.status === 200) {
        const { userId, username: uname, email: uemail, token } = response.data;

        const userData: User = {
          username: uname,
          userId,
          email: uemail || '',
          token
        };

        saveUserData(userData);

        if (isMounted.current) {
          setUser(userData);
          showFeedback('Inloggning lyckades!', 'success');
          setTimeout(onClose, 1000);
        }
      }
    } catch (error: unknown) {
      if (axios.isAxiosError<ErrorResponse>(error)) {
        const message = error.response?.data?.message || 'Fel vid inloggning.';
        showFeedback(message, 'error');
      } else {
        showFeedback('Oväntat fel vid inloggning.', 'error');
        console.error(error);
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleRegister = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!username || !email || !password) {
      showFeedback('Vänligen fyll i alla fält', 'warning');
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(`${apiBaseUrl}/api/auth/register`, { username, email, password });
      if (response.status === 201 || response.status === 200) {
        showFeedback('Registrering lyckades! Du kan nu logga in.', 'success');
        setIsLoginMode(true);
        resetForm();
      }
    } catch (error: unknown) {
      if (axios.isAxiosError<ErrorResponse>(error)) {
        const message = error.response?.data?.message || 'Fel vid registrering.';
        showFeedback(message, 'error');
      } else {
        showFeedback('Oväntat fel vid registrering.', 'error');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const sessionUser = sessionStorage.getItem('user');
    if (sessionUser) {
      const userData: User = JSON.parse(sessionUser);
      setUser(userData);
      setUsername(userData.username);
      setEmail(userData.email);
      return;
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser: User & { expiry?: number } = JSON.parse(storedUser);

      if (parsedUser.expiry && new Date().getTime() <= parsedUser.expiry) {
        sessionStorage.setItem('user', JSON.stringify(parsedUser));
        sessionStorage.setItem('token', parsedUser.token);
        setUser(parsedUser);
        setUsername(parsedUser.username);
        setEmail(parsedUser.email);
      } else {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
      }
    }
  }, [setUser]);

  useEffect(() => () => { isMounted.current = false }, []);

  return (
    <>
      <Dialog open={true} onClose={onClose}>
        <DialogTitle>{isLoginMode ? 'Logga in' : 'Registrera'}</DialogTitle>
        <DialogContent>
          <form onSubmit={isLoginMode ? handleLogin : handleRegister}>
            <TextField
              label="Användarnamn"
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
            {!isLoginMode && (
              <TextField
                label="E-post"
                fullWidth
                margin="normal"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            )}
            <TextField
              label="Lösenord"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isLoginMode ? "current-password" : "new-password"}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ backgroundColor: '#5cb85c', color: 'white', '&:hover': { backgroundColor: '#4cae4c' }, mt: 2 }}
              startIcon={isLoginMode ? (<SlLogin /> as unknown as React.ReactNode) : null} 
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : isLoginMode ? 'Logga in' : 'Registrera'}
            </Button>
          </form>

          <Typography sx={{ mt: 2, fontSize: '0.9rem', textAlign: 'center' }}>
            {isLoginMode ? (
              <>Har du inget konto? <Link component="button" variant="body2" onClick={() => { setIsLoginMode(false); resetForm(); }}>Registrera dig här</Link></>
            ) : (
              <>Har du redan ett konto? <Link component="button" variant="body2" onClick={() => { setIsLoginMode(true); resetForm(); }}>Logga in här</Link></>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">Stäng</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        TransitionComponent={Slide}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
